import { logger } from '../utils/logger';
import { PublicClientApplication } from '@azure/msal-browser';
import { graphRequest } from '../authConfig';

const resolveGraphProxyBase = () => {
  if ((import.meta as any).env?.VITE_GROUNDING_API_BASE) {
    return (import.meta as any).env.VITE_GROUNDING_API_BASE;
  }

  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;

    if (hostname === 'rrrealty.ai' || hostname === 'www.rrrealty.ai') {
      return 'https://fn-conversationsave.azurewebsites.net/api';
    }

    if (hostname === 'testing.rrrealty.ai') {
      return 'https://fn-conversationsave.azurewebsites.net/api';
    }

    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:7071/api';
    }
  }

  return 'https://fn-conversationsave.azurewebsites.net/api';
};

const GRAPH_PROXY_BASE = resolveGraphProxyBase();

export interface Email {
  id: string;
  subject: string;
  from: string;
  fromEmail: string;
  toRecipients: Array<{ emailAddress: { address: string, name: string } }>;
  receivedDateTime: string;
  isRead: boolean;
  hasAttachments: boolean;
  preview: string;
  conversationId?: string;
  importance?: string;
  webLink?: string;
  bodyPreview?: string;
  body?: {
    content: string;
    contentType: 'text' | 'html';
  };
}

export interface EmailThreadMessage {
  id: string;
  subject: string;
  from: string;
  fromEmail: string;
  receivedDateTime: string;
  isRead: boolean;
  bodyPreview: string;
  bodyContent: string;
}

export interface CalendarEvent {
  id: string;
  subject: string;
  start: string;
  startTimeZone: string;
  end: string;
  endTimeZone: string;
  location: string;
  organizer: string;
  attendees: string[];
  isAllDay: boolean;
}

export interface MeetingTimeSuggestion {
  start: string;
  end: string;
  confidence: number;
  organizerAvailability: string;
  attendeeAvailability: Array<{
    email: string;
    availability: string;
  }>;
  reason: string;
}

export interface Person {
  name: string;
  email: string;
}

class GraphService {
  private msalInstance: PublicClientApplication | null = null;

  setMsalInstance(instance: PublicClientApplication) {
    this.msalInstance = instance;
  }

  private async getGraphAccessToken(): Promise<string> {
    if (!this.msalInstance) {
      throw new Error('MSAL instance not set');
    }

    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      throw new Error('No authenticated user');
    }

    try {
      // Try silent token acquisition first
      const response = await this.msalInstance.acquireTokenSilent({
        ...graphRequest,
        account: accounts[0],
      });
      return response.accessToken;
    } catch (error) {
      // Fall back to popup if silent fails
      logger.debug('Silent token acquisition failed, trying popup');
      const response = await this.msalInstance.acquireTokenPopup(graphRequest);
      return response.accessToken;
    }
  }

  async searchEmailsKql(options: {
    kql?: string;
    from?: string;
    subject?: string;
    startDate?: string;
    endDate?: string;
    size?: number;
    maxPages?: number;
    includeJunk?: boolean;
  }): Promise<{ emails: Email[]; count: number; totalReported: number; moreAvailable: boolean; kql: string }> {
    const accessToken = await this.getGraphAccessToken();
    const response = await fetch(`${GRAPH_PROXY_BASE}/graph/emailssearch`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(options),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || `Email search failed (${response.status})`);
    }

    const data = await response.json();
    return {
      emails: data.emails,
      count: data.count,
      totalReported: data.totalReported ?? data.count,
      moreAvailable: data.moreAvailable ?? false,
      kql: data.kql ?? '',
    };
  }

  async getEmails(options: {
    top?: number;
    unreadOnly?: boolean;
    filter?: string;
    from?: string;
    to?: string;
    startDateTime?: string;
    endDateTime?: string;
    hasAttachments?: boolean;
    importance?: 'low' | 'normal' | 'high';
    search?: string;
    includeBody?: boolean;
  } = {}): Promise<{ emails: Email[]; count: number }> {
    const accessToken = await this.getGraphAccessToken();
    
    const params = new URLSearchParams();
    if (options.top) params.set('top', options.top.toString());
    if (options.unreadOnly) params.set('unreadOnly', 'true');
    if (options.filter) params.set('filter', options.filter);
    if (options.from) params.set('from', options.from);
    if (options.to) params.set('to', options.to);
    if (options.startDateTime) params.set('startDateTime', options.startDateTime);
    if (options.endDateTime) params.set('endDateTime', options.endDateTime);
    if (options.hasAttachments) params.set('hasAttachments', 'true');
    if (options.importance) params.set('importance', options.importance);
    if (options.search) params.set('search', options.search);
    if (options.includeBody) params.set('includeBody', 'true');

    const response = await fetch(`${GRAPH_PROXY_BASE}/graph/emails?${params}`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch emails');
    }

    const data = await response.json();
    return { emails: data.emails, count: data.count };
  }

  async getEmailThread(conversationId: string): Promise<{ thread: Email[] }> {
    if (!conversationId) {
      return { thread: [] };
    }

    const accessToken = await this.getGraphAccessToken();
    const response = await fetch(`${GRAPH_PROXY_BASE}/graph/emailthread?conversationId=${encodeURIComponent(conversationId)}&includeBody=true`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch email thread');
    }

    const data = await response.json();
    return { thread: data.thread || [] };
  }

  async getCalendarEvents(options: {
    startDateTime?: string;
    endDateTime?: string;
  } = {}): Promise<{ events: CalendarEvent[]; count: number }> {
    const accessToken = await this.getGraphAccessToken();
    
    const params = new URLSearchParams();
    if (options.startDateTime) params.set('startDateTime', options.startDateTime);
    if (options.endDateTime) params.set('endDateTime', options.endDateTime);

    const response = await fetch(`${GRAPH_PROXY_BASE}/graph/calendar?${params}`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch calendar events');
    }

    const data = await response.json();
    return { events: data.events, count: data.count };
  }

  async findMeetingTimes(options: {
    attendees: string[];
    durationMinutes?: number;
    startDateTime?: string;
    endDateTime?: string;
  }): Promise<{ suggestions: MeetingTimeSuggestion[]; count: number; emptySuggestionsReason: string | null }> {
    const accessToken = await this.getGraphAccessToken();

    const response = await fetch(`${GRAPH_PROXY_BASE}/graph/findmeetingtimes`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        attendees: options.attendees,
        durationMinutes: options.durationMinutes || 60,
        startDateTime: options.startDateTime,
        endDateTime: options.endDateTime,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to find meeting times');
    }

    const data = await response.json();
    return {
      suggestions: data.suggestions,
      count: data.count,
      emptySuggestionsReason: data.emptySuggestionsReason,
    };
  }

  // Helper to format emails for AI context
  formatEmailsForContext(emails: Email[]): string {
    if (emails.length === 0) return 'No emails found.';
    
    return emails.map((email, i) => {
      const date = new Date(email.receivedDateTime).toLocaleString();
      return `${i + 1}. From: ${email.from} (${email.fromEmail})
   Subject: ${email.subject}
   Date: ${date}
   Read: ${email.isRead ? 'Yes' : 'No'}
   Preview: ${email.preview}`;
    }).join('\n\n');
  }

  // Helper to format calendar events for AI context
  formatEventsForContext(events: CalendarEvent[]): string {
    if (events.length === 0) return 'No calendar events found.';
    
    return events.map((event, i) => {
      const start = new Date(event.start).toLocaleString();
      const end = new Date(event.end).toLocaleString();
      const attendeeList = event.attendees.length > 0 ? event.attendees.join(', ') : 'None';
      return `${i + 1}. ${event.subject}
   Time: ${start} - ${end}
   Location: ${event.location || 'Not specified'}
   Organizer: ${event.organizer}
   Attendees: ${attendeeList}
   All Day: ${event.isAllDay ? 'Yes' : 'No'}`;
    }).join('\n\n');
  }

  // Helper to format meeting time suggestions for AI context
  formatSuggestionsForContext(suggestions: MeetingTimeSuggestion[]): string {
    if (suggestions.length === 0) return 'No available meeting times found.';
    
    return suggestions.map((suggestion, i) => {
      const start = new Date(suggestion.start).toLocaleString();
      const end = new Date(suggestion.end).toLocaleString();
      return `${i + 1}. ${start} - ${end}
   Confidence: ${suggestion.confidence}%
   Reason: ${suggestion.reason || 'Available'}`;
    }).join('\n\n');
  }

  async searchPeople(query: string): Promise<{ people: Person[]; count: number }> {
    const accessToken = await this.getGraphAccessToken();

    const response = await fetch(`${GRAPH_PROXY_BASE}/graph/people?q=${encodeURIComponent(query)}`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to search people');
    }

    const data = await response.json();
    return { people: data.people, count: data.count };
  }

  // Resolve a name to an email address
  async resolveNameToEmail(name: string): Promise<string | null> {
    try {
      const { people } = await this.searchPeople(name);
      if (people.length > 0) {
        return people[0].email;
      }
      return null;
    } catch (error) {
      logger.error('Failed to resolve name:', name, error);
      return null;
    }
  }
}

export const graphService = new GraphService();
